import type { ProjectListItem } from '@/entities/project';
import type { ProjectsViewState } from './ProjectsViewState.types';

interface ResolveParams {
  isLoading: boolean;
  isError: boolean;
  rows: ProjectListItem[];
  hasSearch: boolean;
  hasActiveFilters: boolean;
  onNewProject: () => void;
  onClearFilters: () => void;
}

/**
 * Resolves the current view state for the Projects list page.
 *
 * Returns a discriminated union so the renderer can switch
 * on `state.type` without nested ternaries.
 */
export function resolveProjectsViewState(params: ResolveParams): ProjectsViewState {
  if (params.isError) return { type: 'error' };

  // During loading or when there are rows, show the data view
  // (the table handles its own loading state internally)
  if (params.isLoading || params.rows.length > 0) {
    return { type: 'data', rows: params.rows } as ProjectsViewState;
  }

  // Empty + active search/filters → "no matching projects"
  if (params.hasSearch || params.hasActiveFilters) {
    return { type: 'no-results', onClear: params.onClearFilters };
  }

  // Truly empty → "no projects created"
  return { type: 'empty', onAction: params.onNewProject };
}
